import type { QrStyleState } from './types'

export type Preset = { name: string; config: Omit<QrStyleState, 'url'> }

// Export size is always 512px; preview is rendered at a separate smaller size.
const EXPORT_SIZE = 512

export const presets: Preset[] = [
  {
    // Matches the geometry of the 3D-printed raised-module plaque and stencil:
    // circular dots at ~0.4× module size, rounded-square finder rings, EC L.
    // What you see in the preview is what the STL encodes.
    name: '3D-print',
    config: {
      size: EXPORT_SIZE,
      margin: 8,
      errorCorrection: 'L',
      shape: 'square',
      dotsType: 'dots',
      dotsColor: '#000000',
      dotsRoundSize: true,
      dotsGradientEnabled: false,
      dotsGradientType: 'linear',
      dotsGradientRotation: 0,
      dotsGradientStartColor: '#000000',
      dotsGradientEndColor: '#000000',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#000000',
      cornersSquareGradientEnabled: false,
      cornersSquareGradientType: 'linear',
      cornersSquareGradientRotation: 0,
      cornersSquareGradientStartColor: '#000000',
      cornersSquareGradientEndColor: '#000000',
      cornersDotType: 'dot',
      cornersDotColor: '#000000',
      backgroundColor: '#ffffff',
      backgroundGradientEnabled: false,
      backgroundGradientType: 'linear',
      backgroundGradientRotation: 0,
      backgroundGradientStartColor: '#ffffff',
      backgroundGradientEndColor: '#ffffff',
      logoEnabled: false,
      logoImage: '',
      logoSize: 0.35,
      logoMargin: 6,
      logoHideBackgroundDots: true,
    },
  },
  {
    name: 'Standard',
    config: {
      size: EXPORT_SIZE,
      margin: 8,
      errorCorrection: 'L',
      shape: 'square',
      dotsType: 'rounded',
      dotsColor: '#000000',
      dotsRoundSize: true,
      dotsGradientEnabled: false,
      dotsGradientType: 'linear',
      dotsGradientRotation: 0,
      dotsGradientStartColor: '#000000',
      dotsGradientEndColor: '#000000',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#000000',
      cornersSquareGradientEnabled: false,
      cornersSquareGradientType: 'linear',
      cornersSquareGradientRotation: 0,
      cornersSquareGradientStartColor: '#000000',
      cornersSquareGradientEndColor: '#000000',
      cornersDotType: 'dot',
      cornersDotColor: '#000000',
      backgroundColor: '#ffffff',
      backgroundGradientEnabled: false,
      backgroundGradientType: 'linear',
      backgroundGradientRotation: 0,
      backgroundGradientStartColor: '#ffffff',
      backgroundGradientEndColor: '#ffffff',
      logoEnabled: false,
      logoImage: '',
      logoSize: 0.35,
      logoMargin: 6,
      logoHideBackgroundDots: true,
    },
  },
  {
    name: 'Warm Geo',
    config: {
      size: EXPORT_SIZE,
      margin: 8,
      errorCorrection: 'L',
      shape: 'square',
      dotsType: 'rounded',
      dotsColor: '#0b3b4a',
      dotsRoundSize: true,
      dotsGradientEnabled: true,
      dotsGradientType: 'linear',
      dotsGradientRotation: 25,
      dotsGradientStartColor: '#0b3b4a',
      dotsGradientEndColor: '#f08a5d',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#1e4f5b',
      cornersSquareGradientEnabled: true,
      cornersSquareGradientType: 'radial',
      cornersSquareGradientRotation: 0,
      cornersSquareGradientStartColor: '#1e4f5b',
      cornersSquareGradientEndColor: '#f8c291',
      cornersDotType: 'dot',
      cornersDotColor: '#f08a5d',
      backgroundColor: '#fef7f0',
      backgroundGradientEnabled: false,
      backgroundGradientType: 'linear',
      backgroundGradientRotation: 0,
      backgroundGradientStartColor: '#fef7f0',
      backgroundGradientEndColor: '#fef7f0',
      logoEnabled: false,
      logoImage: '',
      logoSize: 0.35,
      logoMargin: 6,
      logoHideBackgroundDots: true,
    },
  },
  {
    name: 'Atlantic Glow',
    config: {
      size: EXPORT_SIZE,
      margin: 8,
      errorCorrection: 'L',
      shape: 'circle',
      dotsType: 'classy-rounded',
      dotsColor: '#0c4a4a',
      dotsRoundSize: true,
      dotsGradientEnabled: true,
      dotsGradientType: 'radial',
      dotsGradientRotation: 0,
      dotsGradientStartColor: '#0c4a4a',
      dotsGradientEndColor: '#6cd4c6',
      cornersSquareType: 'square',
      cornersSquareColor: '#0c4a4a',
      cornersSquareGradientEnabled: false,
      cornersSquareGradientType: 'linear',
      cornersSquareGradientRotation: 0,
      cornersSquareGradientStartColor: '#0c4a4a',
      cornersSquareGradientEndColor: '#0c4a4a',
      cornersDotType: 'rounded',
      cornersDotColor: '#6cd4c6',
      backgroundColor: '#f4fbfa',
      backgroundGradientEnabled: false,
      backgroundGradientType: 'linear',
      backgroundGradientRotation: 0,
      backgroundGradientStartColor: '#f4fbfa',
      backgroundGradientEndColor: '#f4fbfa',
      logoEnabled: false,
      logoImage: '',
      logoSize: 0.35,
      logoMargin: 6,
      logoHideBackgroundDots: true,
    },
  },
  {
    name: 'Citrus Punch',
    config: {
      size: EXPORT_SIZE,
      margin: 8,
      errorCorrection: 'L',
      shape: 'square',
      dotsType: 'extra-rounded',
      dotsColor: '#ff6f3c',
      dotsRoundSize: true,
      dotsGradientEnabled: true,
      dotsGradientType: 'linear',
      dotsGradientRotation: 310,
      dotsGradientStartColor: '#ff6f3c',
      dotsGradientEndColor: '#ffd460',
      cornersSquareType: 'dot',
      cornersSquareColor: '#ff6f3c',
      cornersSquareGradientEnabled: false,
      cornersSquareGradientType: 'linear',
      cornersSquareGradientRotation: 0,
      cornersSquareGradientStartColor: '#ff6f3c',
      cornersSquareGradientEndColor: '#ff6f3c',
      cornersDotType: 'dot',
      cornersDotColor: '#ffd460',
      backgroundColor: '#fff7e0',
      backgroundGradientEnabled: true,
      backgroundGradientType: 'radial',
      backgroundGradientRotation: 0,
      backgroundGradientStartColor: '#fff7e0',
      backgroundGradientEndColor: '#ffe1b3',
      logoEnabled: false,
      logoImage: '',
      logoSize: 0.35,
      logoMargin: 6,
      logoHideBackgroundDots: true,
    },
  },
]
